import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import Recuperacion from './Recuperacion';

const IOS = {
  bg: '#F2F2F7', surface: '#FFFFFF',
  separator: 'rgba(60,60,67,0.12)',
  label: '#1C1C1E', label2: 'rgba(60,60,67,0.55)', label3: 'rgba(60,60,67,0.25)',
  blue: '#007AFF', purple: '#5E5CE6', green: '#34C759',
  red: '#FF3B30', orange: '#FF9500', teal: '#5AC8FA',
};

const imagenes = [
  { src: '/imagenes/imagen1.png', pos: 'center 70%',   srcMovil: '/imagenes/imagen1-movil.png', posMovil: 'right center' },
  { src: '/imagenes/imagen2.png', pos: 'center 70%',   srcMovil: '/imagenes/imagen2-movil.png', posMovil: 'center center' },
  { src: '/imagenes/imagen3.png', pos: 'center 80%',   srcMovil: '/imagenes/imagen3-movil.png', posMovil: 'center center' },
  { src: '/imagenes/imagen4.png', pos: 'center bottom', srcMovil: '/imagenes/imagen4-movil.png', posMovil: 'center center' },
];

const obtenerDeviceId = () => {
  let id = localStorage.getItem('device_id');
  if (!id) { id = crypto.randomUUID(); localStorage.setItem('device_id', id); }
  return id;
};

// ── Protección brute-force local ──────────────────────────────────────────────
const BF_KEY        = 'login_bf';
const MAX_INTENTOS  = 5;      // intentos antes de bloqueo local
const COOLDOWNS     = [0, 0, 3, 10, 30, 60]; // segundos de espera por intento fallido (idx = intento)

const getBf = () => {
  try { return JSON.parse(localStorage.getItem(BF_KEY) || '{}'); } catch { return {}; }
};
const setBf = (data) => localStorage.setItem(BF_KEY, JSON.stringify(data));
const resetBf = () => localStorage.removeItem(BF_KEY);

// Retorna segundos de bloqueo restantes (0 = libre)
const getBloqueoRestante = () => {
  const bf = getBf();
  if (!bf.bloqueadoHasta) return 0;
  const resta = Math.ceil((new Date(bf.bloqueadoHasta) - Date.now()) / 1000);
  return resta > 0 ? resta : 0;
};

const registrarFallo = () => {
  const bf = getBf();
  const intentos = (bf.intentos || 0) + 1;
  const cooldown = COOLDOWNS[Math.min(intentos, COOLDOWNS.length - 1)];
  const bloqueadoHasta = cooldown > 0
    ? new Date(Date.now() + cooldown * 1000).toISOString()
    : null;
  setBf({ intentos, bloqueadoHasta });
  return { intentos, cooldown };
};

const Login = () => {
  const navigate = useNavigate();
  const [formData,   setFormData]   = useState({ usuario: '', contrasena: '' });
  const [showPass,   setShowPass]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [bgImg,      setBgImg]      = useState(null);
  const [mostrarRec, setMostrarRec] = useState(false);
  const [msg,        setMsg]        = useState({ open: false, text: '', severity: 'info' });

  // Brute-force state
  const [bfBloqueado,  setBfBloqueado]  = useState(false);
  const [bfCountdown,  setBfCountdown]  = useState(0);
  const [bfIntentos,   setBfIntentos]   = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setBgImg(imagenes[Math.floor(Math.random() * imagenes.length)]);

    // Verificar si ya hay bloqueo activo al cargar
    const resta = getBloqueoRestante();
    if (resta > 0) iniciarCountdown(resta);
    const bf = getBf();
    if (bf.intentos) setBfIntentos(bf.intentos);

    return () => clearInterval(timerRef.current);
  }, []);

  const iniciarCountdown = (segundos) => {
    setBfBloqueado(true);
    setBfCountdown(segundos);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setBfCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setBfBloqueado(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (bfBloqueado) return;
    if (!formData.usuario.trim() || !formData.contrasena.trim()) {
      showMsg('Ingrese usuario y contraseña', 'warning'); return;
    }

    // Verificar bloqueo local antes de llamar al servidor
    const resta = getBloqueoRestante();
    if (resta > 0) { iniciarCountdown(resta); return; }

    setLoading(true);
    try {
      const deviceId = obtenerDeviceId();
      const response = await authService.login(
        formData.usuario.trim(), formData.contrasena.trim(), deviceId
      );
      if (response.success) {
        resetBf(); // limpiar contadores en login exitoso
        if (response.requiereCambioPassword) {
          showMsg('Debe cambiar su contraseña antes de continuar', 'warning');
          setTimeout(() => navigate('/cambio-password-obligatorio'), 800);
          return;
        }
        showMsg('¡Bienvenido!', 'success');
        setTimeout(() => navigate('/dashboard'), 500);
      }
    } catch (error) {
      // Registrar fallo localmente
      const { intentos, cooldown } = registrarFallo();
      setBfIntentos(intentos);

      if (intentos >= MAX_INTENTOS) {
        const bloqueoPermanente = 300; // 5 min si supera el máximo
        setBf({ intentos, bloqueadoHasta: new Date(Date.now() + bloqueoPermanente * 1000).toISOString() });
        iniciarCountdown(bloqueoPermanente);
        showMsg(`Demasiados intentos. Espera ${Math.ceil(bloqueoPermanente / 60)} minutos.`, 'error');
      } else if (cooldown > 0) {
        iniciarCountdown(cooldown);
        showMsg(
          `${error.message || 'Credenciales incorrectas'}. Espera ${cooldown}s antes de reintentar.`,
          error.type || 'error'
        );
      } else {
        const restantes = MAX_INTENTOS - intentos;
        showMsg(
          `${error.message || 'Credenciales incorrectas'}${restantes <= 2 ? ` — ${restantes} intento${restantes !== 1 ? 's' : ''} restante${restantes !== 1 ? 's' : ''}` : ''}`,
          error.type || 'error'
        );
      }
    } finally { setLoading(false); }
  };

  const showMsg = (text, severity) => setMsg({ open: true, text, severity });

  // ── Estilos input — fix autocomplete webkit ──────────────────────────────
  const inputBase = {
    width: '100%', height: 48,
    padding: '0 14px 0 44px',
    border: '1.5px solid rgba(255,255,255,0.18)',
    borderRadius: 12,
    fontSize: '0.92rem',
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.10)',
    outline: 'none',
    boxSizing: 'border-box',
    backdropFilter: 'blur(4px)',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s, background-color 0.2s',
    // ── Fix autocomplete: forzar fondo oscuro y texto blanco ──
    WebkitTextFillColor: 'white',
    caretColor: 'white',
  };

  const inputPassBase = { ...inputBase, paddingRight: 48 };

  // Inyectar estilos globales para webkit-autofill una sola vez
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'login-autofill-fix';
    if (!document.getElementById('login-autofill-fix')) {
      style.textContent = `
        /* Fix iOS safe area — evitar franjas blancas */
        html, body {
          overscroll-behavior: none;
        }
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0px 1000px rgba(30,30,50,0.85) inset !important;
          -webkit-text-fill-color: white !important;
          caret-color: white !important;
          border-color: rgba(255,255,255,0.18) !important;
          transition: background-color 99999s ease-in-out 0s;
        }
        /* Quitar el ojito nativo del browser en inputs password */
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear,
        input[type="password"]::-webkit-contacts-auto-fill-button,
        input[type="password"]::-webkit-credentials-auto-fill-button {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById('login-autofill-fix');
      if (el) el.remove();
    };
  }, []);

  // Fix iOS viewport height — agrega --vh real al root
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  const bloqueadoTotal = bfBloqueado || loading;

  return (
    <>
      {/* Fondo imagen */}
      <Box sx={{
        minHeight: 'calc(var(--vh, 1vh) * 100)',
        width: '100vw', position: 'relative',
        backgroundImage: bgImg
          ? { xs: `url(${bgImg.srcMovil || bgImg.src})`, md: `url(${bgImg.src})` }
          : 'none',
        backgroundSize: 'cover',
        backgroundPosition: bgImg
          ? { xs: bgImg.posMovil || 'center center', md: bgImg.pos }
          : 'center bottom',
        backgroundRepeat: 'no-repeat',
        // Fondo de fallback negro para que no salga blanco en safe area iOS
        backgroundColor: '#1C1C1E',
        display: 'flex', alignItems: 'center',
        justifyContent: { xs: 'center', md: 'flex-start' },
        pl: { xs: 0, md: '6vw' },
        px: { xs: 2, sm: 0 },
      }}>
        {/* Overlay */}
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 1,
          background: {
            xs: 'rgba(0,0,0,0.52)',
            md: 'linear-gradient(105deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.48) 42%, rgba(0,0,0,0.12) 100%)'
          }
        }} />

        {/* Panel login */}
        <Box sx={{ position: 'relative', zIndex: 2, width: { xs: '100%', sm: 400 }, maxWidth: { xs: 420, sm: 400 } }}>
          {mostrarRec ? (
            <Recuperacion onVolver={() => setMostrarRec(false)} />
          ) : (
            <Box>
              {/* Logo */}
              <Box sx={{ mb: 5 }}>
                <img src="/imagenes/logo-integra.png" alt="integra"
                  style={{ height: 36, display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              </Box>

              {/* Saludo */}
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: { xs: '2rem', sm: '2.4rem' }, fontWeight: 800, color: 'white', lineHeight: 1.15, letterSpacing: '-0.03em' }}>
                  Bienvenido
                </Typography>
                <Typography sx={{ fontSize: { xs: '2rem', sm: '2.4rem' }, fontWeight: 800, color: IOS.purple, lineHeight: 1.15, letterSpacing: '-0.03em' }}>
                  de vuelta.
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.55)', mt: 1.2, fontWeight: 400 }}>
                  Ingresa tus credenciales para continuar
                </Typography>
              </Box>

              {/* Banner bloqueo */}
              {bfBloqueado && (
                <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '12px',
                  bgcolor: 'rgba(255,59,48,0.15)', border: '1px solid rgba(255,59,48,0.35)',
                  display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'rgba(255,59,48,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF3B30" strokeWidth="2.2" strokeLinecap="round">
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#FF3B30' }}>
                      Acceso bloqueado temporalmente
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)' }}>
                      Reintenta en <b style={{ color: 'white' }}>{bfCountdown}s</b>
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Formulario */}
              <form onSubmit={handleSubmit} autoComplete="on">

                {/* Usuario */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.8 }}>
                    Usuario
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex', alignItems: 'center', zIndex: 1 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinecap="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </Box>
                    <input
                      name="usuario"
                      type="text"
                      autoFocus
                      autoComplete="username"
                      placeholder="Tu nombre de usuario"
                      value={formData.usuario}
                      onChange={handleChange}
                      disabled={bloqueadoTotal}
                      style={{ ...inputBase, opacity: bloqueadoTotal ? 0.5 : 1 }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.6)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.14)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.10)'; }}
                    />
                  </Box>
                </Box>

                {/* Contraseña */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.8 }}>
                    Contraseña
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex', alignItems: 'center', zIndex: 1 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinecap="round">
                        <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </Box>
                    <input
                      name="contrasena"
                      type={showPass ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Tu contraseña"
                      value={formData.contrasena}
                      onChange={handleChange}
                      disabled={bloqueadoTotal}
                      style={{ ...inputPassBase, opacity: bloqueadoTotal ? 0.5 : 1 }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.6)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.14)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.10)'; }}
                    />
                    {/* UN SOLO OJITO — el nuestro, el nativo desactivado por CSS */}
                    <Box component="button" type="button"
                      onClick={() => setShowPass(!showPass)}
                      disabled={bloqueadoTotal}
                      sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                        bgcolor: 'transparent', border: 'none', cursor: bloqueadoTotal ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', p: 0.5, borderRadius: '50%', zIndex: 2,
                        '&:hover': { bgcolor: bloqueadoTotal ? 'transparent' : 'rgba(255,255,255,0.1)' } }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinecap="round">
                        {showPass
                          ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                          : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                        }
                      </svg>
                    </Box>
                  </Box>
                </Box>

                {/* Olvidé contraseña */}
                <Box sx={{ textAlign: 'right', mb: 4 }}>
                  <Typography component="span" onClick={() => setMostrarRec(true)}
                    sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', transition: 'color 0.15s', '&:hover': { color: 'rgba(255,255,255,0.85)' } }}>
                    ¿Olvidaste tu contraseña?
                  </Typography>
                </Box>

                {/* Botón ingresar */}
                <Box component="button" type="submit"
                  disabled={bloqueadoTotal}
                  sx={{
                    width: '100%', height: 50, borderRadius: '14px', border: 'none',
                    bgcolor: bfBloqueado
                      ? 'rgba(255,59,48,0.25)'
                      : loading
                        ? 'rgba(255,255,255,0.15)'
                        : IOS.purple,
                    cursor: bloqueadoTotal ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    transition: 'all 0.2s',
                    boxShadow: bloqueadoTotal ? 'none' : `0 8px 24px ${IOS.purple}60`,
                    '&:hover': { filter: bloqueadoTotal ? 'none' : 'brightness(1.1)', transform: bloqueadoTotal ? 'none' : 'translateY(-1px)' },
                    '&:active': { transform: 'translateY(0)' },
                  }}>
                  {loading && (
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', animation: 'spin 0.8s linear infinite',
                      '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                  )}
                  {bfBloqueado && (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round">
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  )}
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, letterSpacing: '0.02em',
                    color: bloqueadoTotal ? 'rgba(255,255,255,0.4)' : 'white' }}>
                    {bfBloqueado
                      ? `Bloqueado (${bfCountdown}s)`
                      : loading
                        ? 'Ingresando...'
                        : 'Ingresar'
                    }
                  </Typography>
                </Box>

                {/* Indicador de intentos — solo si hay alguno */}
                {bfIntentos > 0 && !bfBloqueado && (
                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', gap: 0.6 }}>
                    {Array.from({ length: MAX_INTENTOS }, (_, i) => (
                      <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%',
                        bgcolor: i < bfIntentos ? IOS.red : 'rgba(255,255,255,0.15)',
                        transition: 'background-color 0.3s' }} />
                    ))}
                  </Box>
                )}
              </form>
            </Box>
          )}
        </Box>

        {/* Logo ARDATA */}
        <Box sx={{ position: 'fixed', bottom: { xs: 12, sm: 20 }, right: { xs: 16, sm: 24 }, zIndex: 10, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Box component="a" href="https://www.ardata.com.pe/" target="_blank" rel="noopener noreferrer"
            sx={{
              position: 'relative', display: 'block', lineHeight: 0, cursor: 'pointer',
              width: window.innerWidth < 600 ? 65 : 82,
              transition: 'transform 0.45s cubic-bezier(0.25, 0.8, 0.25, 1)',
              '&:hover': { transform: 'translateY(-2px)' },
              '& img': { width: '100%', height: 'auto', display: 'block' },
              '& .logo-base': {
                opacity: 0.5,
                transition: 'opacity 0.45s cubic-bezier(0.25, 0.8, 0.25, 1)',
              },
              '& .logo-blanco': {
                position: 'absolute', top: 0, left: 0,
                filter: 'brightness(0) invert(1)',
                opacity: 0,
                transition: 'opacity 0.45s cubic-bezier(0.25, 0.8, 0.25, 1)',
              },
              '&:hover .logo-base': { opacity: 0 },
              '&:hover .logo-blanco': { opacity: 1 },
            }}>
            <img className="logo-base" src="/imagenes/logo-ardata.png" alt="ARDATA"
              onError={e => e.target.parentElement.style.display = 'none'} />
            <img className="logo-blanco" src="/imagenes/logo-ardata.png" alt=""
              aria-hidden="true" />
          </Box>
          <Typography sx={{ fontSize: { xs: '7px', sm: '8px' }, color: 'rgba(255,255,255,0.3)' }}>
            © 2026 ardata, todos los derechos reservados
          </Typography>
        </Box>
      </Box>

      {/* Mensajes */}
      <Snackbar open={msg.open} autoHideDuration={6000} onClose={() => setMsg(m => ({ ...m, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setMsg(m => ({ ...m, open: false }))} severity={msg.severity} variant="filled" sx={{ width: '100%' }}>
          {msg.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Login;
